// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import * as moment from "moment";
import "moment/locale/fr";
import { FHIRHelper } from "../helpers/FHIR-helper";
import { Tools } from "../helpers/tools-helper";
import { CycleSchema } from "./entitylink";
import { IGoal } from "./goal";

import {
  ACTION_STATUS_ENTITY,
  Codes,
  Coding,
  IEntity,
  Identifier,
  Location,
  Period,
  Quantity,
  Reference,
  STATUS_ENTITY,
  ScheduledTiming,
  Timing,
} from "./sharedInterfaces";
import { FileLogger } from "../helpers/fileLogger";

/**
 * gather meta info on careplan templates
 */
export interface ICareplanTemplate {
  provider: Reference; // organization owning these careplans
  careplans: ICareplan[];
  performers: IPerformer[]; // careprovider performing these activity careplan, group by healthcare service
}

export interface IPerformer {
  healthcareService: Reference;
  practitioners: Reference[];
}

// Convert FHIR xml to json with https://fhir-formats.github.io/
// Genetated from Json data with: http://json2ts.com/

export interface Progress {
  text: string;
  authorReference: Reference;
  time: Date;
}

export interface Detail {
  status: string;
  location: Location | Reference; // custom Location instead of Reference
  description: string;
  prohibited?: boolean; // generate appointment or not (default: true)
  category: Codes;
  code: Codes;
  goal?: IGoal[];
  productReference?: Reference;
  productTemplateList?: Reference[]; // NOT FHIR: list of possible products. Used in template
  performer?: Reference[];
  scheduledTiming?: ScheduledTiming; // "ScheduledTiming.code" is used to store drugs take time  (M for morning, N for noon, E for evening)
  // ex: "ME" means drug has to  be taken in morning and evening
  scheduledPeriod?: Period;
  scheduledString?: string;
  quantity?: Quantity;
  dailyAmount?: Quantity;
  cycle?: CycleSchema; // WARNING! This is only needed if the activity has a drug and it's to help link changes between careplan cycle and entitylink drugs cycle
  // DO NOT USE this to get the drug cycle !!! Download the entityDrug to get the cycle !!!
}

export interface Activity {
  _id: string; // extra param, not in FHIR
  actionResulting?: IOrder[]; // link a sub-Activity to an careplan Activity with specific timing (Day-1, Day+2, etc.)
  progress?: Progress[];
  reference?: Reference;
  detail?: Detail;
}
/**
 * Inteface to link a sub-Activity to an careplan Activity with specific timing (Day-1, Day+2, etc.)
 */
export interface IOrder {
  identifier: Identifier;
  status: string; // same as activity
  date: string; // 	When the order was made
  source?: Reference; // Who initiated the order
  target?: Reference; // Who is intended to fulfill the order
  reasonCodeableConcept: Coding; // why the order was made
  when: Timing; // 	When order should be fulfilled
  location?: Location; // What action is being ordered
  detail: IOrderAction; // where action should be fulfilled
}

/**
 * Request/order for activity
 */
export interface IOrderAction {
  target: ACTION_TARGET;
  reference: Reference;
}

/**
 * kind of action to do ?
 */
export enum ACTION_TARGET {
  OBSERVATION = 1,
  QUESTIONNAIRE = 2,
  RECOMMEND = 3,
  KNOWLEDGE = 4,
  COMMUNICATION = 5,
}

/**
 * Careplan
 */
export class ICareplan extends IEntity {
  identifier: Identifier[];
  status: string; // draft,inactive,active,archive
  period: Period;
  description: string;
  resourceType: string;
  participant: Reference[];
  goal?: IGoal[];
  activity: Activity[];
  category: Codes[];
  support?: Reference[]; // template reference (use to link to questionnaires and observations)
  context?: Reference; // used in Connectors to identifier the context of creation (currently CAREPLANCHUSP & CAREPLANCHULG)
  subject: Reference;
  note?: string[];
  author: Reference[];
  contributor: ContributorReference[];
  addresses?: IAddresses[];
}

export interface CareplanContributorReference {
  careplanName: string; // le nom du careplan déjà traduit (cf ce qui est affiché dans le menu de gauche de la mobileApp)
  contributor: ContributorReference[];
}

export interface ContributorReference {
  type: ContributorReferenceType;
  display: string;
}

export enum ContributorReferenceType {
  PRACTITIONNER = "Practitioner",
  HEALTHCARESERVICE = "HealthcareService",
  ORGANIZATION = "Organization",
}
export interface IAddresses {
  reference: string;
  display: string; // $TR$
  status: string; // inactive, active
  recommendations: IPath[];
}
export interface IPath {
  path: string;
}

/**
 * Care plan model of data
 */
export class Careplan {
  // careplan status
  public static get CAREPLAN_STATUS_ACTIVE(): string {
    return "active";
  }
  public static get CAREPLAN_STATUS_INACTIVE(): string {
    return "inactive";
  }
  public static get CAREPLAN_STATUS_DRAFT(): string {
    return "draft";
  }
  public static get CAREPLAN_STATUS_ARCHIVE(): string {
    return "archive";
  }
  // activity status field
  public static get ACTIVITY_STATUS_ACTIVE(): string {
    return "active";
  }
  public static get ACTIVITY_STATUS_INACTIVE(): string {
    return "inactive";
  }
  public static get ACTIVITY_STATUS_ERROR(): string {
    return "error";
  }
  // careplan context
  public static get CONTEXT_CHU_LG(): string {
    return "CAREPLANCHULG";
  }
  public static get CONTEXT_CHU_SP(): string {
    return "CAREPLANCHUSP";
  }
  public static get CONTEXT_PATIENT(): string {
    return "CAREPLANPATIENT";
  }

  /**
   * Return owner (patient target) of this careplan
   * @param careplan
   */
  public static getOwnerIdentifier(careplan: ICareplan): string {
    try {
      return careplan.subject.reference;
    } catch (e) {
      return "";
    }
  }

  /**
   *  return Activity by its ID
   */
  public static getCaremateIdentifier(careplan: ICareplan): string {
    if (!careplan) return null;
    for (let i = 0; i < careplan.identifier.length; i++) {
      if (
        careplan.identifier[i].system === "usual" ||
        careplan.identifier[i].system === FHIRHelper.SYSTEM_CAREMATE ||
        careplan.identifier[i].system === "caremate"
      ) {
        if (careplan.identifier[i].value !== "") return careplan.identifier[i].value;
      }
    }
    return careplan.identifier.length > 0 ? careplan.identifier[0].value : "";
  }

  /**
   *  search for Activity by its ID
   */
  /*public getActivityById(id: string): Activity {
        for (let activity of this.activity) {
            if (activity._id === id) return activity;
        }
        return null;
    }*/

  /**
   * return display name of this care plan
   */
  public static getDisplayName(cp: ICareplan): string {
    try {
      if (!cp) return "";
      // return cp.category[0].coding[0].display;
      return cp.description;
    } catch (err) {
      FileLogger.error("Careplan", "getDisplayName:", err);
      return cp.description;
    }
  }

  /**
   *  convert a data string (JSON) of careplans to a array of Careplan object
   */
  /*public static fromJson(data: any): Array<Careplan> {
        let careplans: Array<Careplan> = new Array<Careplan>();
        try {
            if (data instanceof Array) {
                data.forEach(element => {
                    let careplan = new Careplan(element);
                    careplans.push(careplan);
                });
            }
        }
        catch (ex) {
        }
        return careplans;
    }*/

  /**
   * Returns "true" if careplan category is LINEAR
   * @param cp
   */
  public static isLinear(cp: ICareplan): boolean {
    try {
      for (const cat of cp.category) {
        if (cat.text && cat.text.startsWith("LINEAR/")) return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  }

  /**
   * Prepare a new Careplan from a careplan template.
   * Method not used
   * @param template
   * @deprecated
   */
  public static careplanFromTemplate(
    template: ICareplan,
    organization: Reference,
    service: Reference,
    startDate: string,
    account: Reference
  ): ICareplan {
    try {
      const newCareplan = Tools.deepCopy(template);
      // set ID
      newCareplan._id = undefined;
      // set identifiers
      newCareplan.identifier = [
        {
          use: "usual",
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: organization.reference + "/" + moment().format("X"),
        },
        {
          system: FHIRHelper.SYSTEM_ORGANIZATION,
          value: organization.reference,
          use: service.reference,
        },
      ];
      // set status
      newCareplan.status = Careplan.CAREPLAN_STATUS_ACTIVE;
      // set patient as subject
      newCareplan.subject = account;
      // start date for careplan
      if (startDate) {
        newCareplan.period.start = startDate;
        // set schedule activity timing
        newCareplan.activity.forEach((activity) => {
          Careplan.setActivityStartDate(activity, startDate);
          // Careplan.setActivityEndDate(activity, startDate);
        });
      }
      // set creation context: store that this careplan was created by a patient
      newCareplan.context = {
        reference: Careplan.CONTEXT_PATIENT,
        display: "identify a careplan created by patient",
      };
      // set internal status
      newCareplan.entityStatus = [STATUS_ENTITY.ACTIVE];
      newCareplan.actionStatus = ACTION_STATUS_ENTITY.CREATED;
      // set creation date
      newCareplan.creation = moment().format();
      newCareplan.modified = moment().format();
      return newCareplan;
    } catch (err) {
      FileLogger.error("Careplan", "careplanFromTemplate", err);
      return null;
    }
  }

  /**
   * Set start (and end) date for a activity
   * @param activity
   * @param dateValue
   */
  public static setActivityStartDate(activity: Activity, dateValue: string): void {
    try {
      if (Careplan.isScheduledTiming(activity)) {
        activity.detail.scheduledTiming.repeat.boundsPeriod.start = dateValue;
      } else if (Careplan.isScheduledPeriod(activity)) {
        activity.detail.scheduledPeriod.start = dateValue;
      } else if (Careplan.isScheduledString(activity)) {
        activity.detail.scheduledString = dateValue;
      }
    } catch (err) {
      FileLogger.error("Careplan", "Error in setActivityStartDate", err);
    }
  }

  /**
   * Set end date for a activity
   * @param activity
   * @param dateValue
   */
  public static setActivityEndDate(activity: Activity, dateValue: string): void {
    try {
      if (Careplan.isScheduledTiming(activity)) {
        activity.detail.scheduledTiming.repeat.boundsPeriod.end = dateValue;
      } else if (Careplan.isScheduledPeriod(activity)) {
        activity.detail.scheduledPeriod.end = dateValue;
      } else if (Careplan.isScheduledString(activity)) {
        // nothing to do
      }
    } catch (err) {
      FileLogger.error("Careplan", "Error setActivityEndDate setActivityStartDate", err);
    }
  }

  /**
   * Activity is scheduled with timing
   * @param activity
   */
  public static isScheduledTiming(activity: Activity): boolean {
    return activity.detail.scheduledTiming !== undefined;
  }

  /**
   * Activity is scheduled with period
   * @param activity
   */
  public static isScheduledPeriod(activity: Activity): boolean {
    return activity.detail.scheduledPeriod !== undefined;
  }

  /**
   * Activity is not scheduled but a one shot
   * @param activity
   */
  public static isScheduledString(activity: Activity): boolean {
    return activity.detail.scheduledString !== undefined;
  }

  /**
   * Set activity protocol
   * @param activity
   * @param protocol
   */
  public static setProtocol(activity: Activity, protocol: Reference): void {
    activity.detail.productReference.reference = protocol.reference;
    activity.detail.productReference.display = protocol.display;
  }
}
